import { useMemo } from 'react';
import { compact, noop } from 'lodash';

import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { PatientMedicineQuery } from '@dreryk/egabinet-prescription-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { commonTranslationKeys, Ordering } from '@dreryk/react-common';
import { TextPattern } from '@dreryk/watermelon';
import {
   Button,
   ButtonVariant,
   createDynamicColumnsWidth,
   Padding,
   TableColumnConfiguration,
} from '@dreryk/react-components';

import { PatientMedicine } from '~libs.shared.prescription';
import { protectFeature } from '~libs.shared.system';
import { VisitDiagnoses } from '~libs.shared.visit';
import {
   MedicineActiveIngredients,
   MedicineDetailsModalOpenAction,
   MedicineName,
   MedicinePackage,
   MedicineReimbursementSelector,
   MedicineSafetyIndicators,
} from '~libs.desktop.shared.medicine';

import { medicineSelectorTranslationKeys } from '../../../../../../../translations';

const COMMERCIAL_MEDICINE_NAME_COLUMN = 'medicineFullName';

// TODO: DREGAB-1669 set valid type when mechanism to sort on custom fields will be available
const DEFAULT_ORDERINGS: Ordering<any>[] = [
   {
      property: COMMERCIAL_MEDICINE_NAME_COLUMN,
      descending: false,
   },
];
export const useMedicineSearchDataConfiguration = (
   patientId: Patient['id'],
   searchText: string
): LazyLoadedDataConfigurationByCqrsQuery<PatientMedicine> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PatientMedicine>(() => {
      const query = new PatientMedicineQuery();
      query.searchText = TextPattern.anywhereIgnoreCase(searchText ?? undefined);
      query.patientId = patientId;
      return query;
   }, DEFAULT_ORDERINGS);
};

type UseeMedicineSearchTableColumnConfigurationOptions = {
   patientId: Patient['id'];
   onMedicineSelection: (medicine: PatientMedicine) => void;
   visitDiagnoses: VisitDiagnoses;
};

// eslint-disable-next-line max-lines-per-function
export const useMedicineSearchTableColumnConfiguration = (
   options: UseeMedicineSearchTableColumnConfigurationOptions
): TableColumnConfiguration<PatientMedicine>[] => {
   // eslint-disable-next-line max-lines-per-function
   return useMemo<TableColumnConfiguration<PatientMedicine>[]>((): TableColumnConfiguration<PatientMedicine>[] => {
      const medicineDatabaseExtendedAvailable = protectFeature('extendedMedicineDatabase');

      const columnsWidths = createDynamicColumnsWidth({
         [COMMERCIAL_MEDICINE_NAME_COLUMN]: medicineDatabaseExtendedAvailable ? '65%' : '50%',
         safetyIndicators: medicineDatabaseExtendedAvailable ? '82px' : '0px',
         package: medicineDatabaseExtendedAvailable ? '35%' : '50%',
         options: '78px',
      });

      return compact([
         {
            name: COMMERCIAL_MEDICINE_NAME_COLUMN,
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.commercialName,
            width: columnsWidths[COMMERCIAL_MEDICINE_NAME_COLUMN],

            renderer: (props) => (
               <div>
                  <MedicineDetailsModalOpenAction
                     medicine={props.bean.medicine}
                     render={(openModal) => (
                        <MedicineName
                           name={props.bean.medicine.fullName}
                           atc={props.bean.medicine.atc}
                           onClick={openModal}
                        />
                     )}
                  />
                  <MedicineActiveIngredients activeIngredients={props.bean.medicine.activeIngredients} />
                  <MedicineReimbursementSelector
                     reimbursements={props.bean.reimbursements}
                     disabled={false}
                     visitDiagnoses={options.visitDiagnoses}
                  />
               </div>
            ),
         },
         medicineDatabaseExtendedAvailable
            ? {
                 name: 'safetyIndicators',
                 accessor: noop,
                 width: columnsWidths.safetyIndicators,
                 renderer: (props) => <MedicineSafetyIndicators medicineId={props.bean.medicine.id} />,
                 sortingDisabled: true,
              }
            : undefined,
         {
            name: 'package',
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.package,
            width: columnsWidths.package,
            renderer: (props) => (
               <MedicinePackage
                  form={props.bean.medicine.form}
                  dose={props.bean.medicine.dose}
                  packageContent={props.bean.medicine.packageContent}
               />
            ),
            sortingDisabled: true,
         },
         {
            name: 'options',
            accessor: noop,
            label: '',
            renderer: (props) => (
               <Button
                  testId={'chooseMedicine'}
                  label={commonTranslationKeys.choose}
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => options.onMedicineSelection(props.bean)}
                  type={'button'}
                  compact
               />
            ),
            width: columnsWidths.options,
            sortingDisabled: true,
            padding: Padding.NONE,
         },
      ]);
   }, [options]);
};
